import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../lib/csrf"
import {
  toDefinition, summarize, formatFlat, parseFlat, conditionProblem, definitionProblems, unitsPerReport, canonicalJson
} from "../lib/workflow_definition"
import { createEditableDesigner, loadDesigner, toolboxGroups } from "../lib/workflow_designer"

const SELECT = "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
const LABEL = "block text-sm font-medium text-gray-700 dark:text-gray-300"

// Fixed choices for a transmit step's only_when; a stored map that is none of these shows as an
// extra option so nothing is lost.
const ONLY_WHEN = [
  { label: "always", value: null },
  { label: "severity is critical", value: { severity: ["critical"] } },
  { label: "severity is high or critical", value: { severity: ["critical", "high"] } },
  { label: "validity is valid", value: { validity: ["valid"] } },
  { label: "scope is in_scope", value: { scope: ["in_scope"] } },
  { label: "duplicate is false", value: { duplicate: ["false"] } }
]

// The Workflow step of the channel wizard (channel processing spec 3.6 and 3.8): the version bar,
// the designer, the text view, and the Versions, Commit, and Dry run overlays. The working
// definition lives in the hidden field the wizard form posts; Commit, Discard, Restore, Dry run,
// and Versions are fetch calls because a frame swap would destroy the editor.
export default class extends Controller {
  static targets = [
    "canvas", "field", "visualPane", "textPane", "textBox", "textStatus", "modeButton",
    "dirty", "summary", "commitButton", "commitTitle", "commitConfirm", "commitChanges", "commitErrors", "commitNote",
    "versionLabel", "runningPill", "overlay", "versionsList", "dryRunReport", "dryRunResult"
  ]
  static values = {
    definition: Object,
    integrations: Array,
    publicKeys: Array,
    runningVersion: Object,
    nextNumber: Number,
    editing: Boolean,
    versionsUrl: String,
    commitUrl: String,
    discardUrl: String,
    restoreUrl: String,
    dryRunUrl: String
  }

  async connect() {
    this.running = this.editing()
    this.mode = "visual"
    this.onTheme = () => this.rebuild(this.current())
    this.onKeydown = (event) => this.keydown(event)
    document.addEventListener("theme:changed", this.onTheme)
    document.addEventListener("keydown", this.onKeydown, { capture: true })
    await this.build(this.definitionValue)
  }

  disconnect() {
    document.removeEventListener("theme:changed", this.onTheme)
    document.removeEventListener("keydown", this.onKeydown, { capture: true })
    this.designer?.destroy()
    this.designer = null
  }

  editing() {
    return this.editingValue ? (this.runningVersionValue.definition || { steps: {} }) : null
  }

  // ---- designer ----

  async build(definition) {
    const SWD = await loadDesigner()
    this.designer?.destroy()
    try {
      this.designer = await createEditableDesigner(this.canvasTarget, definition, {
        toolboxGroups: toolboxGroups(() => SWD.Uid.next()),
        validator: (step, _parent, drawing) => this.validate(step, drawing),
        stepEditorProvider: (step, context) => this.stepEditor(step, context),
        rootEditorProvider: () => this.rootEditor()
      })
    } catch (error) {
      this.textStatusTarget.textContent = error.message
      this.textStatusTarget.className = "text-xs text-red-600"
      this.showText(definition)
      return
    }
    this.designer.onDefinitionChanged.subscribe(() => this.changed())
    this.changed()
  }

  async rebuild(definition, { keep = true } = {}) {
    const selected = keep && this.designer ? this.designer.getSelectedStepId() : null
    const viewport = keep && this.designer ? this.designer.getViewport() : null
    await this.build(definition)
    // The undo stack does not survive a rebuild; selection and viewport do.
    if (selected) { try { this.designer.selectStepById(selected) } catch (_e) { /* the step may be gone */ } }
    if (viewport) this.designer.onReady.subscribe(() => this.designer?.setViewport(viewport))
  }

  current() {
    if (this.mode === "text") {
      try { return parseFlat(this.textBoxTarget.value) } catch (_e) { return this.lastFlat || { steps: {} } }
    }
    return this.designer ? toDefinition(this.designer.getDefinition()) : (this.lastFlat || { steps: {} })
  }

  changed() {
    const flat = this.current()
    this.lastFlat = flat
    this.fieldTarget.value = JSON.stringify(flat)
    if (!this.editingValue) return
    const summary = summarize(this.running, flat)
    const has = summary.sentences.length > 0
    this.dirtyTarget.classList.toggle("hidden", !has)
    this.dirtyTarget.classList.toggle("inline-flex", has)
    this.summaryTarget.classList.toggle("hidden", !has)
    this.summaryTarget.textContent = has ? `Since v${this.runningVersionValue.number}: ${summary.sentence}` : ""
    // Validity comes from the definition itself; the designer's badges show the same problems.
    const valid = definitionProblems(flat).length === 0
    this.commitButtonTarget.disabled = !has || !valid
    this.commitButtonTarget.title = has && !valid ? "Fix the marked steps first" : ""
  }

  // The designer's per-step validator, drawn as a badge. Only a transmit step needs the whole
  // definition (its condition must be produced by a step before it).
  validate(step, drawing) {
    if (step.componentType !== "task") return true
    if (step.type !== "transmit") return true
    if (!(step.properties.integration && step.properties.integration.id)) return false
    const [flat, name] = this.nameOf(step, drawing)
    return !name || conditionProblem(flat, name) === null
  }

  // The flat definition of a drawing and the name the given step has in it.
  nameOf(step, drawing) {
    const ids = {}
    const flat = toDefinition(drawing, ids)
    return [flat, Object.keys(ids).find((n) => ids[n] === step.id)]
  }

  // ---- the right panel ----

  rootEditor() {
    const root = document.createElement("div")
    root.className = "p-4 text-sm text-gray-500 dark:text-gray-400"
    root.textContent = "Select a step to edit it."
    return root
  }

  // The designer renders the panel once per selection, so fields that depend on another field
  // (instructions on the custom scale, the key on encrypted_body) re-render the panel themselves.
  stepEditor(step, context) {
    const root = document.createElement("div")
    root.className = "p-4 space-y-4 text-sm"
    const render = () => {
      root.replaceChildren()
      this.fillStepEditor(root, step, context, render)
    }
    render()
    return root
  }

  fillStepEditor(root, step, context, rerender) {
    const head = document.createElement("div")
    head.className = "text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
    head.textContent = step.type
    root.appendChild(head)
    const set = (key) => (value) => { step.properties[key] = value; context.notifyPropertiesChanged() }

    if (step.componentType === "task") {
      const name = document.createElement("input")
      name.type = "text"
      name.className = `${SELECT} font-mono`
      name.value = step.name
      name.dataset.field = "name"
      name.addEventListener("input", () => { step.name = name.value; context.notifyNameChanged() })
      root.appendChild(this.field("Name", name))
    }

    if (step.type === "triage") {
      const task = step.properties.task
      if (task === "scope") {
        root.appendChild(this.field("Engine", this.select([["rule", "rule"], ["ai", "ai"]], step.properties.engine, set("engine"), "engine")))
      } else if (task === "duplicate") {
        const current = step.properties.engine === "rule" ? "rule" : (step.properties.method === "prompt" ? "prompt" : "embeddings")
        root.appendChild(this.field("Method", this.select([["embeddings", "ai, embeddings"], ["prompt", "ai, prompt"], ["rule", "rule, title match"]], current, (v) => {
          if (v === "rule") { step.properties.engine = "rule"; delete step.properties.method } else { step.properties.engine = "ai"; step.properties.method = v }
          context.notifyPropertiesChanged()
        }, "method")))
        root.appendChild(this.field("Lookback", this.select([["30", "30 days"], ["90", "90 days"], ["180", "180 days"], ["365", "365 days"]], String(step.properties.lookback_days || 90), (v) => set("lookback_days")(parseInt(v, 10)), "lookback_days")))
      } else if (task === "severity") {
        root.appendChild(this.field("Scale", this.select([["standard", "standard"], ["custom", "custom"]], step.properties.scale || "standard", (v) => {
          step.properties.scale = v
          if (v !== "custom") delete step.properties.instructions
          context.notifyPropertiesChanged()
          rerender()
        }, "scale")))
        if (step.properties.scale === "custom") {
          const instructions = document.createElement("textarea")
          instructions.className = SELECT
          instructions.rows = 4
          instructions.maxLength = 2000
          instructions.dataset.field = "instructions"
          instructions.value = step.properties.instructions || ""
          instructions.addEventListener("input", () => set("instructions")(instructions.value))
          root.appendChild(this.field("Instructions", instructions))
        }
      }
      const units = document.createElement("p")
      units.className = "text-xs text-gray-500 dark:text-gray-400"
      units.textContent = step.properties.engine === "rule" ? "No units" : "1 unit per report"
      root.appendChild(units)
    } else if (step.type === "transmit") {
      root.appendChild(this.field("Integration", this.integrationSelect(step, context)))
      root.appendChild(this.field("Public key", this.keySelect(step, context)))
      root.appendChild(this.field("Cloaked reports", this.select([["send", "Send"], ["drop", "Drop"]], step.properties.cloaked || "send", (v) => {
        step.properties.cloaked = v
        context.notifyPropertiesChanged()
      }, "cloaked")))
      root.appendChild(this.field("Only when", this.onlyWhenSelect(step, context)))
      const problem = this.problemFor(step)
      if (problem) {
        const warning = document.createElement("p")
        warning.className = "text-xs text-amber-700 dark:text-amber-400"
        warning.dataset.field = "problem"
        warning.textContent = problem
        root.appendChild(warning)
      }
    } else {
      const note = document.createElement("p")
      note.className = "text-xs text-gray-500 dark:text-gray-400"
      note.textContent = step.type === "parallel" ? "Steps inside run at the same time." : "Steps inside run one after another."
      root.appendChild(note)
    }
  }

  integrationSelect(step, context) {
    const select = document.createElement("select")
    select.className = SELECT
    select.dataset.field = "integration"
    const current = step.properties.integration || {}
    const blank = document.createElement("option")
    blank.value = ""
    blank.textContent = "Choose an integration"
    select.appendChild(blank)
    const groups = [["Webhook", "Webhooks"], ["LocalApp", "Local apps"]]
    groups.forEach(([type, label]) => {
      const items = this.integrationsValue.filter((i) => i.type === type)
      if (!items.length) return
      const group = document.createElement("optgroup")
      group.label = label
      items.forEach((i) => {
        const option = document.createElement("option")
        option.value = `${i.type}:${i.id}`
        option.textContent = i.name
        if (current.type === i.type && String(current.id) === String(i.id)) option.selected = true
        group.appendChild(option)
      })
      select.appendChild(group)
    })
    select.addEventListener("change", () => {
      const [type, id] = select.value.split(":")
      step.properties.integration = type ? { type, id } : { type: "", id: "" }
      context.notifyPropertiesChanged()
    })
    return select
  }

  onlyWhenSelect(step, context) {
    const options = ONLY_WHEN.map((o) => [o.value ? canonicalJson(o.value) : "", o.label])
    const stored = step.properties.only_when
    const storedKey = stored && Object.keys(stored).length ? canonicalJson(stored) : ""
    if (storedKey && !options.some(([v]) => v === storedKey)) {
      options.push([storedKey, Object.entries(stored).map(([k, v]) => `${k} in ${[].concat(v).join(", ")}`).join(" and ")])
    }
    return this.select(options, storedKey, (v) => {
      if (v) step.properties.only_when = JSON.parse(v)
      else delete step.properties.only_when
      context.notifyPropertiesChanged()
    }, "only_when")
  }

  problemFor(step) {
    if (!this.designer) return null
    const [flat, name] = this.nameOf(step, this.designer.getDefinition())
    return name ? conditionProblem(flat, name) : null
  }

  // Optional on a transmit: no key sends the report as it stands, a key encrypts the body to it.
  // None removes the property rather than storing an empty string, which would read as a key.
  keySelect(step, context) {
    const keys = this.publicKeysValue.map((k) => [k.id, k.name])
    return this.select([["", "None"], ...keys], step.properties.public_key_id || "", (value) => {
      if (value) step.properties.public_key_id = value
      else delete step.properties.public_key_id
      context.notifyPropertiesChanged()
    }, "public_key_id")
  }

  field(label, control) {
    const wrap = document.createElement("div")
    const l = document.createElement("label")
    l.className = LABEL
    l.textContent = label
    wrap.appendChild(l)
    wrap.appendChild(control)
    return wrap
  }

  select(options, value, onChange, field) {
    const select = document.createElement("select")
    select.className = SELECT
    if (field) select.dataset.field = field
    options.forEach(([v, label]) => {
      const option = document.createElement("option")
      option.value = v
      option.textContent = label
      if (v === value) option.selected = true
      select.appendChild(option)
    })
    select.addEventListener("change", () => onChange(select.value))
    return select
  }

  // ---- graph and text views ----

  setMode(event) {
    const mode = event.currentTarget.dataset.mode
    if (mode === this.mode) return
    if (mode === "text") {
      this.showText(this.current())
    } else if (!this.applyText()) {
      return
    }
    this.mode = mode
    this.visualPaneTarget.classList.toggle("hidden", mode !== "visual")
    this.textPaneTarget.classList.toggle("hidden", mode !== "text")
    this.modeButtonTargets.forEach((button) => {
      const active = button.dataset.mode === mode
      button.setAttribute("aria-pressed", String(active))
      button.classList.toggle("bg-gray-100", active)
      button.classList.toggle("text-gray-900", active)
      button.classList.toggle("bg-white", !active)
      button.classList.toggle("text-gray-500", !active)
    })
  }

  showText(definition) {
    this.textBoxTarget.value = formatFlat(definition)
    this.status("Valid", false)
    this.mode = "text"
    this.visualPaneTarget.classList.add("hidden")
    this.textPaneTarget.classList.remove("hidden")
  }

  // Parses the text view into the designer (which updates the hidden field through `changed`);
  // false when it does not parse, in which case the text view stays and the last parsed
  // definition remains in the field. Runs on blur, so it has run before Next is clicked.
  applyText() {
    let definition
    try {
      definition = parseFlat(this.textBoxTarget.value)
    } catch (error) {
      this.status(error.message, true)
      return false
    }
    this.status("Valid", false)
    this.rebuild(definition, { keep: false })
    return true
  }

  textBlur() {
    if (this.mode === "text") this.applyText()
  }

  status(message, error) {
    this.textStatusTarget.textContent = message
    this.textStatusTarget.className = error ? "text-xs text-red-600" : "text-xs text-gray-500 dark:text-gray-400"
  }

  // ---- overlays ----

  open(event) {
    const name = event.currentTarget.dataset.overlay
    this.closeOverlays()
    const overlay = this.overlayTargets.find((o) => o.dataset.overlay === name)
    if (!overlay) return
    overlay.classList.remove("hidden")
    if (name === "versions") this.loadVersions()
    if (name === "commit") this.prepareCommit()
    if (name === "dryrun") this.runDryRun()
  }

  close() {
    this.closeOverlays()
  }

  closeOverlays() {
    this.overlayTargets.forEach((o) => o.classList.add("hidden"))
  }

  openOverlay() {
    return this.overlayTargets.find((o) => !o.classList.contains("hidden"))
  }

  keydown(event) {
    if (event.key === "Escape" && this.openOverlay()) {
      event.stopPropagation()
      event.preventDefault()
      this.closeOverlays()
      return
    }
    // Enter inside the step panel or the text view never submits the wizard form.
    if (event.key === "Enter" && this.element.contains(event.target) && event.target.tagName !== "TEXTAREA" && event.target.tagName !== "BUTTON") {
      event.preventDefault()
    }
  }

  // ---- server calls ----

  headers(accept) {
    return { Accept: accept, "Content-Type": "application/json", "X-CSRF-Token": csrfToken() }
  }


  async loadVersions() {
    this.versionsListTarget.innerHTML = '<li class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Loading</li>'
    const response = await fetch(this.versionsUrlValue, { headers: { Accept: "text/html" } })
    this.versionsListTarget.innerHTML = response.ok ? await response.text() : '<li class="px-4 py-3 text-sm text-red-600">Could not load versions</li>'
  }

  async restore(event) {
    event.preventDefault()
    const versionId = event.currentTarget.dataset.versionId
    const response = await fetch(this.restoreUrlValue, { method: "POST", headers: this.headers("application/json"), body: JSON.stringify({ version_id: versionId }) })
    if (!response.ok) return
    const { definition } = await response.json()
    this.closeOverlays()
    if (this.mode === "text") this.showText(definition)
    await this.rebuild(definition, { keep: false })
  }

  async discard(event) {
    event.preventDefault()
    const response = await fetch(this.discardUrlValue, { method: "POST", headers: this.headers("application/json") })
    if (!response.ok) return
    const { definition } = await response.json()
    if (this.mode === "text") this.showText(definition)
    await this.rebuild(definition, { keep: false })
  }

  prepareCommit() {
    const number = this.nextNumberValue
    this.commitTitleTarget.textContent = `Commit v${number}`
    this.commitConfirmTarget.textContent = `Commit v${number}`
    this.commitErrorsTarget.textContent = ""
    this.commitNoteTarget.textContent = `v${number} runs from the next dispatch. Reports already in flight finish on v${this.runningVersionValue.number}.`
    const flat = this.lastFlat || this.current()
    const summary = summarize(this.running, flat)
    this.commitChangesTarget.replaceChildren(...summary.sentences.map((s) => {
      const li = document.createElement("li")
      li.textContent = s
      return li
    }))
    const units = document.createElement("li")
    units.className = "text-gray-500 dark:text-gray-400"
    units.textContent = `${unitsPerReport(flat)} units per report`
    this.commitChangesTarget.appendChild(units)
  }

  async commit(event) {
    event.preventDefault()
    const flat = this.current()
    const button = event.currentTarget
    button.disabled = true
    try {
      const response = await fetch(this.commitUrlValue, { method: "POST", headers: this.headers("application/json"), body: JSON.stringify({ workflow_definition: flat }) })
      const body = await response.json()
      if (!response.ok) {
        this.commitErrorsTarget.textContent = (body.errors || ["Commit failed"]).join(". ")
        return
      }
      this.runningVersionValue = { ...this.runningVersionValue, number: body.version.number, committed_at: body.version.committed_at, definition: body.definition }
      this.running = body.definition
      this.nextNumberValue = body.version.number + 1
      this.versionLabelTarget.textContent = `Workflow v${body.version.number}`
      this.runningPillTarget.textContent = `Running since ${body.version.committed_on}`
      this.commitButtonTarget.textContent = `Commit v${this.nextNumberValue}`
      this.closeOverlays()
      this.changed()
      this.element.dispatchEvent(new CustomEvent("workflow:committed", { bubbles: true, detail: body.version }))
    } finally {
      button.disabled = false
    }
  }

  async runDryRun() {
    if (!this.hasDryRunReportTarget) return
    const reportId = this.dryRunReportTarget.value
    if (!reportId) { this.dryRunResultTarget.innerHTML = '<p class="text-xs text-gray-500 dark:text-gray-400">No reports yet.</p>'; return }
    this.dryRunResultTarget.innerHTML = '<p class="text-xs text-gray-500 dark:text-gray-400">Running</p>'
    const response = await fetch(this.dryRunUrlValue, { method: "POST", headers: this.headers("text/html"), body: JSON.stringify({ report_id: reportId, workflow_definition: this.current() }) })
    this.dryRunResultTarget.innerHTML = response.ok ? await response.text() : '<p class="text-xs text-red-600">Dry run failed</p>'
  }
}
